import { NgOptimizedImage } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../core/api/api.service';
import { AuthService } from '../../core/auth/auth.service';
import { MultiSesionService } from '../../core/auth/multi-sesion.service';
import { SessionStore } from '../../core/auth/session.store';
import { resolverDestinoPostHandoff } from './handoff-destino';

/**
 * Punto de entrada del SSO: FrontKordata / Checador abren `/cuenta/login/:token`
 * con un token de un solo uso. Opcionalmente `?returnUrl=/0/main/marketplace/2`
 * (HU59) para aterrizar en el detalle de la app en lugar del catálogo.
 */
@Component({
  selector: 'app-token-login',
  imports: [NgOptimizedImage],
  template: `
    <main class="handoff">
      <img
        ngSrc="assets/images/logo.png"
        alt="Kordata"
        width="250"
        height="93"
        priority
        class="handoff__logo"
      />

      @if (error()) {
        <div role="alert" class="text-center">
          <p class="label-kor mb-1">No pudimos validar tu sesión</p>
          <p class="subTitle mb-3">El enlace de acceso expiró o ya fue utilizado.</p>
          <button type="button" class="btn btn-kordata" (click)="volverALogin()">
            Ir a iniciar sesión
          </button>
        </div>
      } @else {
        <div class="text-center" aria-live="polite">
          <div class="spinner-border text-kordata mb-3" role="status">
            <span class="visually-hidden">Validando tu sesión</span>
          </div>
          <p class="subTitle mb-0">Validando tu sesión...</p>
        </div>
      }
    </main>
  `,
  styles: `
    .handoff {
      min-height: 100dvh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 1.25rem;
    }

    .handoff__logo {
      display: block;
      width: min(180px, 70vw);
      height: auto;
      margin-bottom: 1.5rem;
      object-fit: contain;
    }

    @media (min-width: 768px) {
      .handoff {
        padding: 2rem;
      }
    }
  `,
})
export class TokenLoginComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(ApiService);
  private readonly multiSesion = inject(MultiSesionService);
  private readonly session = inject(SessionStore);
  private readonly auth = inject(AuthService);

  protected readonly error = signal(false);

  constructor() {
    const token = this.route.snapshot.paramMap.get('token');

    if (!token) {
      this.error.set(true);
      return;
    }

    this.api.obtenerSesionTokenizada(token).subscribe({
      next: sesion => this.entrar(sesion),
      error: () => this.error.set(true),
    });
  }

  protected volverALogin(): void {
    this.auth.irALoginDeKordata();
  }

  private entrar(sesion: { token?: string } & Record<string, unknown>): void {
    if (!sesion?.token) {
      this.error.set(true);
      return;
    }

    const indice = this.multiSesion.agregar(sesion as never);
    this.session.setIndice(indice);
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    const destino = resolverDestinoPostHandoff(indice, returnUrl);
    // `replaceUrl` evita que el token de un solo uso quede en el historial.
    this.router.navigateByUrl(destino, { replaceUrl: true });
  }
}
